import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../lib/api.js';
import { fmtDate } from '../lib/datetime.js';
import MonthPicker, { prevMonthOfNow } from '../components/MonthPicker.jsx';

export default function Dashboard() {
  const { user } = useAuth();
  // Default the month to the PREVIOUS calendar month — CrewLink data lands
  // after month-end, so "last month's numbers" is the useful default view.
  // Admin can flip back/forward with the picker.
  const [month, setMonth] = useState(prevMonthOfNow());
  const [data, setData] = useState({ devices: [], runs: [], overview: null });
  const [tops, setTops] = useState({ usage: [], booked: [], noShows: [] });
  const [err, setErr] = useState(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      setErr(null);
      try {
        // Sessions / bookings counts now come straight from /stats/overview;
        // dropping the per-page list calls keeps the dashboard snappy and
        // ensures it agrees with the Stats page (single source of truth).
        const [devices, runs, overview, topUsage, topBooked, topNoShows] =
          await Promise.all([
            api.devices.list(),
            api.crossCheck.runs({ month }),
            api.stats.overview({ month }),
            api.stats.topPilots({ month, metric: 'usage', limit: 5 }),
            api.stats.topPilots({ month, metric: 'booked', limit: 5 }),
            api.stats.topPilots({ month, metric: 'no_shows', limit: 5 }),
          ]);
        if (abort) return;
        setData({
          devices: devices.devices,
          runs: runs.runs,
          overview,
        });
        setTops({ usage: topUsage.rows, booked: topBooked.rows, noShows: topNoShows.rows });
      } catch (e) {
        if (!abort) setErr(e.message);
      }
    })();
    return () => { abort = true; };
  }, [month]);

  const activeDevices = data.devices.filter((d) => d.status === 'active').length;
  const totals = data.overview?.totals || {};
  const latestRun = data.runs.find((r) => r.is_latest) || data.runs[0] || null;
  const summary = latestRun?.flags_summary || null;
  const perDevice = data.overview?.per_device || [];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-cathay-700">Hi {user?.display_name}</h1>
          <p className="text-sm text-slate-600 mt-1">
            Month overview for <strong>{month}</strong>.
          </p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </header>
      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
        <Tile
          label="Active iPad devices"
          value={activeDevices}
          hint={`${data.devices.length} total`}
          link="/devices"
        />
        <Tile
          label="Sessions booked"
          value={totals.booked_sessions ?? 0}
          hint="from CrewLink upload"
          link="/bookings"
        />
        <Tile
          label="Sessions used"
          value={totals.sessions ?? 0}
          hint="from iPad sign-in/out"
          link="/calendar"
        />
        <Tile
          label="Crew booked"
          value={totals.unique_booked_pilots ?? 0}
          hint="unique CathayIDs this month"
          link="/users"
        />
        <Tile
          label="Flags from latest run"
          value={summary?.total ?? '—'}
          hint={latestRun ? fmtDate(latestRun.ran_at) : 'no run yet'}
          link="/cross-check"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded shadow-sm border p-4">
          <header className="flex items-center justify-between">
            <h2 className="font-medium text-slate-800">Latest cross-check</h2>
            <Link to="/cross-check" className="text-xs text-cathay-700 hover:underline">View →</Link>
          </header>
          {summary ? (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <MiniTile label="No-shows" value={summary.no_show} tone="red" />
              <MiniTile label="Substitutes" value={summary.substitute} tone="amber" />
              <MiniTile label="Under-use" value={summary.under_use} tone="amber" />
              <MiniTile label="Unbooked" value={summary.unbooked} tone="slate" />
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-2">
              No run yet for this month. Upload bookings + sessions then run the cross-check.
            </p>
          )}
        </section>

        <section className="bg-white rounded shadow-sm border p-4">
          <header className="flex items-center justify-between">
            <h2 className="font-medium text-slate-800">IPT utilisation this month</h2>
            <Link to="/stats" className="text-xs text-cathay-700 hover:underline">Details →</Link>
          </header>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Hours booked vs actually used; <strong>IPT util.</strong> = (crew + non-crew events) ÷ 24h × days.
          </p>
          <table className="min-w-full text-sm mt-3">
            <thead className="text-slate-500 text-xs">
              <tr className="text-left">
                <th className="py-1 font-normal">IPT</th>
                <th className="py-1 font-normal text-right">Booked</th>
                <th className="py-1 font-normal text-right">Actual</th>
                <th className="py-1 font-normal text-right">IPT util.</th>
                <th className="py-1 font-normal text-right">Other (events)</th>
              </tr>
            </thead>
            <tbody>
              {perDevice.map((d) => {
                const events = d.events_by_type || {};
                const eventTotal = Object.values(events).reduce((a, n) => a + n, 0);
                // IPT util uses 24h × days as the denominator and counts BOTH
                // crew sign-in hours and non-crew event hours in the numerator,
                // matching the Stats page formula. Replaces the old
                // d.utilization_pct which divided by 16h × days and could
                // therefore exceed 100% on heavily-used IPTs.
                const days = data.overview?.assumptions?.days_in_month || 30;
                const avail = 24 * days;
                const usedTotal = (d.hours_used || 0) + eventTotal;
                const iptUtil = avail > 0
                  ? Math.round((usedTotal / avail) * 1000) / 10
                  : 0;
                return (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="py-1.5">
                      <div className="font-medium">{d.name}</div>
                      {d.location && (
                        <div className="text-[10px] text-slate-500">{d.location}</div>
                      )}
                    </td>
                    <td className="py-1.5 text-right font-mono text-slate-700">{d.hours_booked} h</td>
                    <td className="py-1.5 text-right font-mono text-slate-700">{d.hours_used} h</td>
                    <td className="py-1.5 text-right font-mono">
                      <span
                        title={`${usedTotal}h of ${avail}h (24h × ${days}d)`}
                        className={
                          'inline-block px-1.5 py-0.5 rounded text-[11px] ' +
                          (iptUtil >= 60
                            ? 'bg-green-50 text-green-700'
                            : iptUtil >= 25
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-slate-100 text-slate-600')
                        }
                      >
                        {iptUtil}%
                      </span>
                    </td>
                    <td
                      className="py-1.5 text-right font-mono text-slate-600 text-[11px]"
                      title={Object.entries(events).map(([k, v]) => `${k}: ${v}h`).join('\n') || 'no events'}
                    >
                      {eventTotal ? `${eventTotal} h` : '—'}
                    </td>
                  </tr>
                );
              })}
              {perDevice.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-center text-slate-500 text-xs">No devices yet.</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {/* Top pilot widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopWidget
          title="Most time in IPT"
          rows={tops.usage}
          valueCol="minutes_used"
          formatter={(m) => `${Math.round((m / 60) * 10) / 10} h`}
          linkTo="/stats"
        />
        <TopWidget
          title="Most booked"
          rows={tops.booked}
          valueCol="hours_booked"
          formatter={(h) => `${h} h`}
          linkTo="/stats"
        />
        <TopWidget
          title="Top no-shows"
          rows={tops.noShows}
          valueCol="no_show_count"
          formatter={(c) => `${c}×`}
          tone="red"
          linkTo="/cross-check"
        />
      </div>
    </div>
  );
}

function TopWidget({ title, rows, valueCol, formatter, tone, linkTo }) {
  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <header className="flex items-center justify-between">
        <h2 className="font-medium text-slate-800">{title}</h2>
        {linkTo && (
          <Link to={linkTo} className="text-xs text-cathay-700 hover:underline">More →</Link>
        )}
      </header>
      <table className="min-w-full mt-2 text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.cathay_id || i} className="border-t border-slate-100">
              <td className="py-1.5 text-slate-400 w-6">{i + 1}.</td>
              <td className="py-1.5 font-mono">{r.cathay_id}</td>
              <td className={
                'py-1.5 text-right font-medium ' + (tone === 'red' ? 'text-red-700' : 'text-slate-800')
              }>
                {formatter(r[valueCol])}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={3} className="py-3 text-center text-slate-500 text-xs">No data yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function Tile({ label, value, hint, link }) {
  const content = (
    <div className="bg-white rounded shadow-sm border p-3 hover:border-cathay-300 transition">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-cathay-700 mt-0.5">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}
function MiniTile({ label, value, tone }) {
  const tones = { red: 'text-red-700 bg-red-50', amber: 'text-amber-700 bg-amber-50', slate: 'text-slate-700 bg-slate-100' };
  return (
    <div className={'rounded p-2 ' + (tone ? tones[tone] : 'bg-slate-50')}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}
